/**
 * Alpha mixing that works on BOTH a literal colour and a theme token (dark mode, KAN-304).
 *
 * Theme tokens are `var(--cvx-…)` strings, so `` `${token}1f` `` (hex + alpha suffix) and `parseInt(hex)`
 * produce invalid CSS. These helpers keep Light pixel-identical (a `#rrggbb` stays an exact hex/rgba) and
 * use `color-mix(in srgb, <colour> N%, transparent)` for everything else — which resolves to the same
 * colour at the same alpha.
 */

const HEX6 = /^#([0-9a-f]{6})$/i;

/** `colour` at `alpha` (0-1). Hex → rgba(); a var()/other colour → color-mix against transparent. */
export function alphaOf(colour, alpha) {
  const m = HEX6.exec(String(colour || '').trim());
  if (m) {
    const n = parseInt(m[1], 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
  }
  const pct = Math.round(alpha * 10000) / 100;
  return `color-mix(in srgb, ${colour} ${pct}%, transparent)`;
}

/**
 * Drop-in for the old `` `${colour}1f` `` idiom: `byteHex` is the two-digit alpha ("1f" ≈ 12%).
 * A `#rrggbb` keeps the exact legacy 8-digit hex; anything else becomes a color-mix of the same alpha.
 */
export function withHexAlpha(colour, byteHex) {
  const s = String(colour || '').trim();
  if (HEX6.test(s)) return `${s}${byteHex}`;
  return alphaOf(s, parseInt(byteHex, 16) / 255);
}
